// Applies the tenant's uploaded "Logo symbol / favicon" (Enterprise Branding)
// to the browser tab. index.html ships with no icon link so the platform's
// own icon never flashes or gets cached on a white-label domain.
//
// Enterprise Branding saves the same upload to both `logoSymbol` and
// `favicon`; older themes may only have `favicon`. Saving also generates a
// square, padded set (`icons.sizes` 32/48/180/192/512); those are preferred,
// with the original upload as the fallback.
//
// Keep the hrefs identical to branding-tenantShell (functionsts
// src/branding/shell.ts) so the browser doesn't re-fetch after boot.

const LINKS = [
  { rel: "icon", id: "tenant-favicon", size: "32" },
  { rel: "apple-touch-icon", id: "tenant-apple-touch-icon", size: "180" },
];

const sharedIcon = (theme) =>
  theme?.logoSymbol?.url ? theme.logoSymbol : theme?.favicon || {};

export const getTenantFaviconUrl = (theme) => sharedIcon(theme)?.url || "";

/** URL for one size: generated square icon when present, else the upload. */
export const getTenantIconUrl = (theme, size) => {
  const icon = sharedIcon(theme);
  return icon?.icons?.sizes?.[size] || icon?.url || "";
};

// Cache-bust per upload: Chrome holds on to favicons aggressively, so a new
// upload must produce a new href.
const withVersion = (src) => {
  let hash = 0;
  for (let i = 0; i < src.length; i += 1) {
    hash = (hash * 31 + src.charCodeAt(i)) | 0;
  }
  return `${src}${src.includes("?") ? "&" : "?"}v=${Math.abs(hash).toString(36)}`;
};

export function applyTenantFavicon(theme, doc = document) {
  if (!getTenantFaviconUrl(theme) || !doc?.head) return;

  // The /pos Register app sets its own home-screen icon in index.html; leave it.
  const isPos = (doc.location?.pathname || "").indexOf("/pos") === 0;

  LINKS.forEach(({ rel, id, size }) => {
    if (rel === "apple-touch-icon" && isPos) return;
    const href = withVersion(getTenantIconUrl(theme, size));

    // Drop any other icon links so the browser can't pick a stale one.
    doc.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => {
      if (el.id !== id) el.remove();
    });
    if (rel === "icon") {
      doc
        .querySelectorAll('link[rel="shortcut icon"]')
        .forEach((el) => el.remove());
    }

    let link = doc.getElementById(id);
    if (!link) {
      link = doc.createElement("link");
      link.id = id;
      link.rel = rel;
      doc.head.appendChild(link);
    }
    if (link.getAttribute("href") !== href) link.setAttribute("href", href);
  });
}
